/**
 * A type-to-search subject picker for tutoring's forms (public and office).
 *
 * A seventy-subject <select> is a scroll, not a choice. This narrows as you
 * type ("bio", "maths", "english lit"), knows the everyday names people use
 * for subjects, shows a short list of the common ones before anything is
 * typed, and when nothing fits offers the typed text itself — so there is no
 * separate "Other" box, and what people pick is still from one list the
 * matching understands.
 *
 * SubjectCombo.attach(input, {
 *     groups:  [{ group, subjects: [] }],   // Subjects.TUTORING_GROUPS
 *     first:   [],                          // shown above the rest, e.g. a tutor's own subjects
 *     onPick:  (value) => {},               // multi pickers use this and clear the box
 *     keepText: true,                       // single pickers keep the chosen name in the box
 * })
 *
 * ARIA combobox pattern: the input owns a listbox; arrows move, Enter picks,
 * Escape closes.
 */
(function (root) {
    'use strict';

    const POPULAR = ['Mathematics', 'English Language', 'English Literature', 'Biology', 'Chemistry', 'Physics',
        'Combined Science', '11+ Preparation', 'History', 'Geography', 'French', 'Spanish', 'Computer Science',
        'Economics', 'Psychology'];

    // What people type → what the list calls it.
    const ALIASES = {
        maths: ['Mathematics', 'Further Mathematics', 'Statistics'], math: ['Mathematics'],
        english: ['English Language', 'English Literature', 'English Language & Literature'],
        science: ['Combined Science', 'Biology', 'Chemistry', 'Physics'], sciences: ['Combined Science'],
        bio: ['Biology'], chem: ['Chemistry'], phys: ['Physics'], cs: ['Computer Science'], computing: ['Computer Science', 'ICT'],
        coding: ['Computer Science'], programming: ['Computer Science'], re: ['Religious Studies'], rs: ['Religious Studies'],
        pe: ['Sports Science & PE'], sport: ['Sports Science & PE'], art: ['Art & Design'], dt: ['Design Technology'],
        econ: ['Economics'], psych: ['Psychology'], mandarin: ['Chinese (Mandarin)'], chinese: ['Chinese (Mandarin)'],
        '11 plus': ['11+ Preparation'], 'eleven plus': ['11+ Preparation'], '11+': ['11+ Preparation', 'Verbal Reasoning', 'Non-Verbal Reasoning'],
        vr: ['Verbal Reasoning'], nvr: ['Non-Verbal Reasoning'], reading: ['Primary English', 'Phonics'], spelling: ['Primary English', 'Phonics'],
    };

    const STYLE_ID = 'subject-combo-style';
    function injectStyle() {
        if (document.getElementById(STYLE_ID)) return;
        const style = document.createElement('style');
        style.id = STYLE_ID;
        // Colours come from whichever page it is on: the public pages' --tp-*
        // tokens, the app's own, then plain fallbacks.
        style.textContent = `
.sc-wrap { position: relative; }
.sc-list { position: absolute; z-index: 50; left: 0; min-width: max(100%, 280px); top: calc(100% + 4px); margin: 0; padding: 6px; list-style: none;
  max-height: 290px; overflow-y: auto; background: var(--tp-card, var(--card-bg, #fff)); color: var(--tp-ink, var(--text, #1e1b2e));
  border: 1px solid var(--tp-line, var(--border, #ddd)); border-radius: 10px; box-shadow: 0 12px 32px rgba(20, 20, 40, 0.14); }
.sc-list[hidden] { display: none; }
.sc-head { padding: 8px 10px 4px; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--tp-muted, var(--text-muted, #666)); }
.sc-opt { display: flex; justify-content: space-between; gap: 12px; padding: 8px 10px; border-radius: 7px; cursor: pointer; font-size: 0.95rem; }
.sc-opt small { color: var(--tp-muted, var(--text-muted, #666)); font-size: 0.8rem; white-space: nowrap; }
.sc-opt[aria-selected="true"] { background: color-mix(in srgb, var(--tp-accent, var(--accent, #3660ff)) 12%, transparent); }
.sc-opt mark { background: none; color: inherit; font-weight: 800; }
.sc-use { border-top: 1px solid var(--tp-line, var(--border, #ddd)); margin-top: 4px; padding-top: 10px; }
.sc-empty { padding: 10px; color: var(--tp-muted, var(--text-muted, #666)); font-size: 0.9rem; }`;
        document.head.appendChild(style);
    }

    let uid = 0;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

    function highlight(name, q) {
        const i = q ? name.toLowerCase().indexOf(q) : -1;
        if (i < 0) return esc(name);
        return `${esc(name.slice(0, i))}<mark>${esc(name.slice(i, i + q.length))}</mark>${esc(name.slice(i + q.length))}`;
    }

    function attach(input, { groups = [], first = [], onPick = null, keepText = true } = {}) {
        injectStyle();
        const all = [];
        const seen = new Set();
        for (const g of groups) for (const s of g.subjects) if (!seen.has(s)) { seen.add(s); all.push({ name: s, group: g.group }); }
        const byLower = new Map(all.map((o) => [o.name.toLowerCase(), o.name]));

        const id = `sc-${++uid}`;
        const wrap = document.createElement('span');
        wrap.className = 'sc-wrap';
        input.parentNode.insertBefore(wrap, input);
        wrap.appendChild(input);
        const list = document.createElement('ul');
        list.className = 'sc-list';
        list.id = `${id}-list`;
        list.setAttribute('role', 'listbox');
        list.hidden = true;
        wrap.appendChild(list);

        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-controls', list.id);
        input.setAttribute('autocomplete', 'off');
        if (!input.placeholder) input.placeholder = 'Start typing a subject';

        let options = [];
        let active = -1;

        function results(q) {
            if (!q) {
                const pinned = first.filter(Boolean);
                const pop = POPULAR.filter((p) => byLower.has(p.toLowerCase()) && !pinned.includes(p));
                return [
                    ...(pinned.length ? [{ head: 'Teaches' }, ...pinned.map((name) => ({ name }))] : []),
                    { head: 'Popular' }, ...pop.slice(0, pinned.length ? 8 : 12).map((name) => ({ name })),
                ];
            }
            // Everyday names first, in the order they are most likely meant
            // (maths → Mathematics before Further Mathematics), then names
            // that start with what was typed, then names that contain it.
            const aliasHits = Object.keys(ALIASES).filter((k) => k === q || (q.length >= 2 && k.startsWith(q)))
                .flatMap((k) => ALIASES[k]);
            const rank = (o) => {
                const a = aliasHits.indexOf(o.name);
                if (a > -1) return a;
                const n = o.name.toLowerCase();
                if (n.startsWith(q)) return 100;
                if (n.split(/[\s(&]+/).some((w) => w.startsWith(q))) return 200;
                return n.includes(q) ? 300 : -1;
            };
            const hits = all.map((o) => ({ ...o, r: rank(o) })).filter((o) => o.r > -1)
                .sort((a, b) => a.r - b.r || a.name.localeCompare(b.name))
                .slice(0, 12)
                .map((o) => ({ name: o.name, group: o.group }));
            // A known nickname ("maths") is not a new subject to add.
            const exact = byLower.has(q) || Object.prototype.hasOwnProperty.call(ALIASES, q);
            return [...hits, ...(exact ? [] : [{ name: input.value.trim(), use: true }])];
        }

        function draw() {
            const q = norm(input.value);
            const items = results(q);
            options = items.filter((x) => !x.head);
            active = options.length ? 0 : -1;
            let n = 0;
            list.innerHTML = items.map((x) => {
                if (x.head) return `<li class="sc-head" role="presentation">${esc(x.head)}</li>`;
                const i = n++;
                if (x.use) return `<li class="sc-opt sc-use" role="option" id="${id}-o${i}" data-i="${i}">Use “${esc(x.name)}”</li>`;
                return `<li class="sc-opt" role="option" id="${id}-o${i}" data-i="${i}"><span>${highlight(x.name, q)}</span>${x.group && q && !x.name.toLowerCase().includes(x.group.toLowerCase()) ? `<small>${esc(x.group)}</small>` : ''}</li>`;
            }).join('') || '<li class="sc-empty">No subjects match</li>';
            mark();
            open(true);
        }

        function mark() {
            list.querySelectorAll('.sc-opt').forEach((li) => li.setAttribute('aria-selected', String(Number(li.dataset.i) === active)));
            if (active >= 0) {
                input.setAttribute('aria-activedescendant', `${id}-o${active}`);
                const li = list.querySelector(`#${id}-o${active}`);
                if (li) li.scrollIntoView({ block: 'nearest' });
            } else {
                input.removeAttribute('aria-activedescendant');
            }
        }

        function open(on) {
            list.hidden = !on;
            input.setAttribute('aria-expanded', String(on));
        }

        function pick(i) {
            const o = options[i];
            if (!o || !o.name) return;
            if (keepText) input.value = o.name;
            if (onPick) onPick(o.name);
            if (!keepText) input.value = '';
            open(false);
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        input.addEventListener('focus', draw);
        input.addEventListener('input', draw);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                if (list.hidden) return draw();
                if (!options.length) return;
                active = (active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
                mark();
            } else if (e.key === 'Enter') {
                if (!list.hidden && active >= 0) {
                    e.preventDefault();
                    pick(active);
                }
            } else if (e.key === 'Escape') {
                if (!list.hidden) { e.preventDefault(); open(false); }
            }
        });
        // mousedown, not click: click fires after blur has closed the list.
        list.addEventListener('mousedown', (e) => {
            const li = e.target.closest('.sc-opt');
            if (!li) return;
            e.preventDefault();
            pick(Number(li.dataset.i));
        });
        input.addEventListener('blur', () => {
            open(false);
            // A typed name or nickname that is on the list becomes the list's
            // spelling: "maths" left in the box is saved as "Mathematics".
            const q = norm(input.value);
            const canon = byLower.get(q) || (ALIASES[q] && ALIASES[q][0]);
            if (keepText && canon) input.value = canon;
        });
        return { input };
    }

    root.SubjectCombo = { attach, POPULAR };
}(typeof globalThis !== 'undefined' ? globalThis : this));
